<template>
    <section class="topup">
        <wallet-form :title="$t('buyChz.usa.howManyUsdBuy')"
            :isButtonDisabled="isButtonDisabled"
            :balance="balance"
            :isButtonShow="true"
            :isSidebarVisible="false"
            :buttonText="$t('buyChz.proceedToPay')"
            :class="['topup', {'topup--disabled': isIframeShown}]"
            :disclaimerConfig="disclaimerConfig"
            @proceedButtonAction="proceedButtonAction"
        >
            <div class="topup__available-balance">
                <span class="text--bold">{{ $t('buyChz.availableBalance') }}</span>
                <span>{{ availableBalanceText }}</span>
            </div>

            <div class="topup__overlay"
                @click="unlockForm"
            >
                <i class="icon-edit" />
                <span>{{ $t('buyChz.editAmount') }}</span>
            </div>

            <div class="topup__inputs topup__inputs--dark">
                <div class="topup__first-row">
                    <div class="symbol-wrapper">
                        <currency-symbol :symbol="centralizedWallet.currency.symbol"
                            :fontSize="['3', '4']"
                            backgroundColor="#002e3c"
                            color="#ffffff"
                            class="symbol-wrapper__symbol"
                        />

                        <span class="symbol-wrapper__text">{{ centralizedWallet.currency.shortCodeFormatted }}</span>
                    </div>

                    <input id="input-fiat"
                        v-autofocus
                        :class="['topup__input', { 'topup__input--invalid': isTopupLimitReached }]"
                        type="number"
                        placeholder="0"
                        :value="fiatValue.formatted"
                        @input="calculateValue($event, false)"
                    >
                </div>

                <span :class="['topup__remaining-limit-text', { 'topup__input--invalid': isTopupLimitReached }]">
                    {{ remainingLimitText }}
                </span>
            </div>

            <div class="topup__suggestions-wrapper">
                <amount-suggestions class="topup__suggestion-amounts"
                    :amounts="topUpSuggestions"
                    :currencySymbol="centralizedWallet.currency.symbol"
                    gap="10"
                    @amountSelect="onSelectSuggestionAmount"
                />

                <div v-if="amountToCompleteFTPurchase"
                    class="topup__required-balance"
                >
                    <span>{{ `${$t('buyChz.requiredBalance')}: ` }}</span>
                    <span class="topup__text--bold">{{ requiredAmountToCompletePurchase }}</span>
                </div>
            </div>
        </wallet-form>

        <wallet-form v-if="isIframeShown"
            :header="$t('buyChz.paymentProcess')"
            :isSidebarVisible="false"
            class="payment-iframe"
        >
            <iframe id="webApp_iframe"
                :src="iframeSrc"
                frameborder="0"
                name="webApp_iframe"
                class="webApp_iframe"
            />
        </wallet-form>
    </section>
</template>

<script>
// 3rd party
import {
    mapGetters,
    mapActions,
} from 'vuex';

import axios from 'axios';

import {
    addMinutes,
    compareAsc,
} from '@shared/utils/dates';

import {
    formatPrice,
} from '@shared/utils/formatter.js';

// Components
import walletForm from '@webapp/components/wallet/wallet-form.vue';
import amountSuggestions from '@webapp/components/amount-suggestions.vue';
import currencySymbol from '@webapp/components/currency-symbol.vue';

// Helpers
import apiUrl from '@webapp/helpers/api-url.js';
import {
    scrollToElement,
} from '@webapp/helpers/generic.js';

// Config
import socios from '@webapp/socios.json';

// Mixins
import amountMixin from '@webapp/helpers/amount-mixin.js';
import walletMixin from '@shared/mixins/wallet-mixin.js';

// Local Types
import {
    StringBuilderElement,
} from '@webapp/components/string-builder';
import localEpicTypes from '@webapp/store/types.js';

// Shared Types
import {
    RegulationType,
} from '@shared/store/modules/wallet/types';
import sharedEpicTypes from '@shared/epics/types.js';

// Shared Modules
import {
    EventNames,
    AdjustParameterKeys,
} from '@shared/utils/analytics-keys';

export default {
    name: 'wallet-topup',
    components: {
        walletForm,
        amountSuggestions,
        currencySymbol,
    },
    mixins: [
        amountMixin,
        walletMixin,
    ],
    props: {
        balance: {
            type: String,
            default: '',
        },

        amountToCompleteFTPurchase: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            mode: 'topup',
            currentRate: 0,
            currentRateDate: null,
            fiatToCrypto: {
                rate: null,
                date: null,
            },
            cryptoToFiat: {
                rate: null,
                date: null,
            },
            iframeSrc: '',
            isIframeShown: false,
            termsTextList: [
                {
                    text: this.$t('topup.terms'),
                    element: StringBuilderElement.Span,
                },
            ],
            topUpSuggestions: ['50', '100', '200', '250'],
        };
    },
    computed: {
        ...mapGetters([
            'locale',
            'kycTierLevel',
            'userId',
            'userDetails',
            'currencyConversionRates',
            'getCurrencyConversion',
            'device',
            'isPartnerApp',
            'partnerAppData',
            'userId',
            'currencies',
        ]),

        ...mapGetters('wallet', [
            'centralizedWallet',
            'regulatoryRestrictionLimits',
            'remainingLedgerCumulativeLimit',
        ]),

        topupConversion() {
            return this.getCurrencyConversion(this.centralizedWallet.currency.id, this.centralizedWallet.altCurrency.id);
        },

        isButtonDisabled() {
            return !Number(this.fiatValue.original) ||
                this.isIframeShown ||
                this.isTopupLimitReached;
        },

        availableBalanceText() {
            const {
                plainBalance = 0,
                currency = {},
            } = this.centralizedWallet;

            return `: ${currency.symbol}${plainBalance}`;
        },

        topupRegulatoryLimits() {
            const {
                [RegulationType.TopupUsd]: topupUsd = {},
                [RegulationType.TopupUsd30Days]: topupUsd30Days = {},
            } = this.regulatoryRestrictionLimits;

            return {
                topupUsd,
                topupUsd30Days,
            };
        },

        isDailyLimitExceeded() {
            return this.hasReachedRegulatoryRestrictionLimit(this.fiatValue.formatted, RegulationType.TopupUsd);
        },

        isMonthlyLimitExceeded() {
            return this.hasReachedRegulatoryRestrictionLimit(this.fiatValue.formatted, RegulationType.TopupUsd30Days);
        },

        remainingDailyLimitText() {
            return this.$t('buyChz.usa.remainingDailyLimit', {
                limit: `$${Math.trunc(this.topupRegulatoryLimits.topupUsd.remainingAmountBeforeLimit || 0)}`,
            });
        },

        remainingMonthlyLimitText() {
            return this.$t('buyChz.usa.remainingMonthlyLimit', {
                limit: `$${Math.trunc(this.topupRegulatoryLimits.topupUsd30Days.remainingAmountBeforeLimit || 0)}`,
            });
        },

        remainingLimitText() {
            if (this.isMonthlyLimitExceeded) {
                return this.remainingMonthlyLimitText;
            }

            return this.remainingDailyLimitText;
        },

        requiredAmountToCompletePurchase() {
            return formatPrice(this.amountToCompleteFTPurchase, this.centralizedWallet.currency.symbol);
        },

        disclaimerConfig() {
            return {
                isHighlighted: this.isTopupLimitReached,
                textList: [
                    this.$t('buyChz.usa.limitDisclaimer', {
                        limit: formatPrice(Math.trunc(this.topupRegulatoryLimits.topupUsd.limitAmount || 0), this.centralizedWallet.currency.shortCode),
                    }) + '\n',
                    this.$t('buyChz.usa.limit30Disclaimer', {
                        limit: formatPrice(Math.trunc(this.topupRegulatoryLimits.topupUsd30Days.limitAmount || 0), this.centralizedWallet.currency.shortCode),
                    }),
                ],
            };
        },

        euroCurrency() {
            return this.currencies
                .find(({
                    type, shortCode,
                }) => type === 'fiat' && shortCode === 'EUR') || {};
        },
        adjustParams() {
            return {
                key: AdjustParameterKeys.USER_ID,
                value: this.userId,
            };
        },
    },
    async mounted() {
        this.showAppLoader({
            isVisible: true,
            hasOpacity: true,
        });

        await this.getWallet({
            chilizCurrencyId: socios.chilizCurrencyId,
        });

        await this.fetchWalletRemainingLedger();

        this.showAppLoader({
            isVisible: false,
        });
    },
    destroyed() {
        window.removeEventListener('message', this.receiveMessage);
    },
    methods: {
        ...mapActions({
            setModal: localEpicTypes.SET_MODAL,
            resetModal: localEpicTypes.RESET_MODAL,
            showAppLoader: localEpicTypes.SET_APP_LOADER,
            getWallet: sharedEpicTypes.GET_WALLET,
        }),

        ...mapActions('wallet', ['fetchWalletRemainingLedger']),

        unlockForm() {
            if (this.isIframeShown) {
                this.isIframeShown = false;
            }
        },

        onSelectSuggestionAmount(amount) {
            this.fiatValue.original = amount;
            this.calculateValue(null);
        },

        async getGeneratedCode() {
            try {
                const { data: response } = await axios.post(`${apiUrl()}/session/auth-token`);

                return response?.data?.code;
            } catch (error) {
                console.error(error);
                throw error;
            }
        },

        async proceedButtonAction() {
            const fiatAmount = this.fiatValue,
                fiatCurrency = this.fiatCurrency;

            const amount = fiatAmount.original;

            if (amount > 0) {
                const total = Number(amount);

                if (this.remainingLedgerCumulativeLimit !== null && (Number(total) > Number(this.remainingLedgerCumulativeLimit))) {
                    this.setModal({
                        isVisible: true,
                        componentToLoad: 'components/wallet/wallet-level-up-modal',
                        data: {
                            kycLevel: this.kycTierLevel,
                            type: 'topup',
                        },
                    });

                    return false;
                }

                // Add 15 minutes to the date returned from when getting the rate since rate expires in 15 minutes from when it is retrieved.
                const expiryRateDate = addMinutes(new Date(this.currentRateDate), 15);

                if (compareAsc(expiryRateDate, new Date()) === -1) {
                    this.isLoadingConversionRates = true;

                    const currenciesFromList = [this.fiatCurrency.id];
                    const currenciesToList = [this.cryptoCurrency.id];

                    await this.walletMixinRefreshCurrencyConversionRates(currenciesFromList, currenciesToList);

                    return false;
                }
                let isJSON = false;

                try {
                    isJSON = JSON.parse([this.device?.uuid])?.data;
                } catch (err) {
                    // Err is already controled with boolean in case user didnt accept cookies
                }

                const euroAmount = (fiatAmount.original * this.getCurrencyConversion(this.fiatCurrency.id, this.euroCurrency.id).rate).toFixed(2);
                const deviceId = isJSON ? isJSON : this.device?.uuid;

                // GA add_to_cart tracking
                this.$webTrackingInstance?.sendEvent(
                    EventNames.AddToCart,
                    [
                        {
                            key: 'currency',
                            value: 'EUR',
                        },
                        {
                            key: 'userId',
                            value: this.userId,
                        },
                        {
                            key: 'device_id',
                            value: deviceId,
                        },
                        {
                            key: 'amount',
                            value: euroAmount,
                        },
                    ]
                );

                // Trigger Adjust tracking event
                this.$webTrackingInstance?.sendEvent(
                    EventNames.TopupInitiated,
                    [
                        this.adjustParams,
                        {
                            key: 'revenue',
                            value: euroAmount,
                        },
                    ]
                );

                this.openIframe({
                    fiatAmount,
                    fiatCurrency,
                    deviceId,
                });


                return true;
            }

            return false;
        },

        async openIframe({
            fiatAmount,
            fiatCurrency,
            deviceId,
        }) {
            const baseUrl = socios.paymentGatewayUrl;

            this.currentRate = Number(this.topupConversion.rate);
            this.currentRateDate = this.topupConversion.date;

            // Get code to send to FrontOffice in query parameters for authentication purposes
            const generatedCode = await this.getGeneratedCode();
            const encodedDate = this.currentRateDate.replace('+', '%2B');

            const queryParams = `&fiatAmount=${fiatAmount.original}
                &euroConversionRate=${this.euroConversionRate || 1}
                &fiatCurrency=${fiatCurrency.name}
                &fiatCurrencyId=${fiatCurrency.id}
                &generatedCode=${generatedCode}
                &userId=${this.userId ? this.userId : this.userDetails.externalUserId}
                &deviceUid=${deviceId}
                &deviceName=${this.device.name}
                &processStartedAt=${encodedDate}
                &locale=${this.locale}
                &webApp=true
                &appType=webapp
                &isFiatFlow=${true}
            `.replace(/\s/g, '');

            this.iframeSrc = `${baseUrl}/payments?${queryParams}`;
            this.isIframeShown = true;
            const amountToShow = formatPrice(fiatAmount.formatted, this.centralizedWallet.currency.symbol);

            window.addEventListener('message', (event) => this.receiveMessage(event, amountToShow));

            this.$nextTick(() => {
                scrollToElement('payment-iframe');
            });
        },

        receiveMessage({ data = {} }, amount = '') {
            if (data.analytics) {
                this.triggerAnalytics(data.analytics);
            }
            if (data.confirmation) {

                if (this.isPartnerApp) {
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.PartnerDeposit,
                        [
                            {
                                key: 'partner',
                                value: this.partnerAppData.id,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'amount',
                                value: amount,
                            },
                        ]
                    );
                }

                this.setModal({
                    isVisible: true,
                    componentToLoad: 'components/payment-confirmation/payment-confirmation',
                    data: {
                        confirmation: data.confirmation,
                        amount,
                    },
                });

                this.isIframeShown = false;
            }
        },
        triggerAnalytics(eventData) {
            const { eventName } = eventData;
            const fiatAmount = this.fiatValue;
            const euroAmount = (fiatAmount.original * this.getCurrencyConversion(this.fiatCurrency.id, this.euroCurrency.id).rate).toFixed(2);

            switch (eventName) {
                // GA payment error
                case 'Topup_Change_amount_error':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'fiatAmount',
                                value: eventData.fiatAmount,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: euroAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'currency',
                                value: eventData.currency,
                            },
                        ]
                    );
                    break;
                // GA payment selection
                case 'Topup_Payment_Providers':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'name',
                                value: eventData.name,
                            },
                            {
                                key: 'cryptoTotalAmount',
                                value: eventData.cryptoTotalAmount,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: euroAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'currency',
                                value: eventData.currency,
                            },
                            {
                                key: 'name_pp',
                                value: eventData.provider,
                            },
                            {
                                key: 'serviceFee',
                                value: eventData.serviceFee,
                            },
                        ]
                    );
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.TopupInitiatedPp,
                        [
                            this.adjustParams,
                            {
                                key: 'revenue',
                                value: euroAmount,
                            },
                        ]
                    );
                    break;
                // GA payment summary
                case 'Topup_Summary':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'name',
                                value: eventData.name,
                            },
                            {
                                key: 'cryptoTotalAmount',
                                value: eventData.cryptoTotalAmount,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: euroAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'currency',
                                value: eventData.currency,
                            },
                            {
                                key: 'serviceFee',
                                value: eventData.serviceFee,
                            },
                        ]
                    );
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.TopupSummary,
                        [
                            this.adjustParams,
                            {
                                key: 'revenue',
                                value: euroAmount,
                            },
                            {
                                key: AdjustParameterKeys.SERVICE_FEE,
                                value: eventData.serviceFee,
                            },
                        ]
                    );
                    break;
                    // GA payment confirmation
                case 'Topup_Payment_Confirmation':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'fiatCurrency',
                                value: eventData.fiatCurrency,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: euroAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'referenceNumber',
                                value: eventData.referenceNumber,
                            },
                            {
                                key: 'device_id',
                                value: eventData.deviceId,
                            },
                        ]
                    );
                    this.$webTrackingInstance?.sendEvent(
                        EventNames.TopupCompleted,
                        [
                            this.adjustParams,
                            {
                                key: 'revenue',
                                value: euroAmount,
                            },
                        ]
                    );
                    break;
                    // GA payment rejection
                case 'Topup_Payment_Rejection':
                    this.$webTrackingInstance?.sendEvent(
                        eventName,
                        [
                            {
                                key: 'fiatCurrency',
                                value: eventData.fiatCurrency,
                            },
                            {
                                key: 'euroGrossAmount',
                                value: euroAmount,
                            },
                            {
                                key: 'userId',
                                value: this.userId,
                            },
                            {
                                key: 'referenceNumber',
                                value: eventData.referenceNumber,
                            },
                            {
                                key: 'device_id',
                                value: eventData.deviceId,
                            },
                        ]
                    );
                    break;
                default: return null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.topup {
    position: relative;

    @include min-width(lg) {
        padding-top: 3rem;
    }

    &--disabled {
        &:hover {
            .topup__overlay {
                display: flex;
            }
        }

        .topup {
            &__inputs,
            &__middle-row {
                opacity: 0.5;
            }
        }
    }

    &__available-balance {
        color: $cyprus-color;
        font-size: 1.2rem;
        padding-bottom: 2rem;
        margin-top: -4rem;

        span:first-child {
            font-weight: bold;
        }
    }

    &__overlay {
        display: none;
        align-items: center;
        justify-content: center;
        color: $white-color;
        position: absolute;
        background: rgba($cyprus-color, 0.9);
        left: 2rem;
        top: 0;
        z-index: 2;
        width: calc(100% - 4rem);
        height: 100%;
        font-weight: bold;
        font-size: 1.8rem;
        border-radius: 1.5rem;
        cursor: pointer;

        i {
            font-size: 4.5rem;
            margin-right: 2rem;
            color: $lima-color;
        }
    }

    &__inputs {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 3.6rem 1.5rem 1.8rem 1.5rem;
        border-radius: 1.2rem;

        @include min-width(lg) {
            padding: 4.6rem 4.5rem 2.3rem 4.5rem;
        }

        &--dark {
            background: $athens-gray-color;
        }
    }

    &__input-wrapper {
        position: relative;
        height: 100%;
        min-width: 5rem;
    }

    &__input {
        font: 700 2.6rem/2.6rem "Nunito";
        border: none;
        background: transparent;
        outline: none;
        text-align: right;
        min-width: 5rem;
        appearance: textfield;
        text-overflow: ellipsis;
        box-shadow: none;
        padding: 0;
        @include input-color($cyprus-color);

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            appearance: none;
            margin: 0;
        }

        @include min-width(xl) {
            font-size: 3.6rem;
            line-height: 3.6rem;
        }

        &--invalid {
            @include input-color($torch-red-color);
        }
    }

    &__first-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    &__remaining-limit-text {
        color: $dark-gray;
        font-size: 1.1rem;
        align-self: flex-end;

        @include min-width(lg) {
            font-size: 1.3rem;
        }
    }

    &__suggestions-wrapper {
        margin: 5rem 0 5rem 0;
        display: flex;
        flex-direction: column;
        gap: 5rem;

        @include min-width(lg) {
            margin: 5rem 0 8rem 0;
        }
    }

    &__required-balance {
        font-size: 1.2rem;

        @include min-width(lg) {
            font-size: 1.6rem;
        }

        span {
            &:first-child {
                color: $cyprus-color;
            }

            &:last-child {
                color: $dark-gray;
            }
        }
    }
}

.symbol-wrapper {
    display: flex;
    align-items: center;
    gap: 1rem;

    @include min-width(lg) {
        gap: 1.5rem;
    }

    &__symbol {
        height: 5rem;
        width: 5rem;
    }

    &__text {
        font-size: 2.6rem;
        font-weight: bold;
        color: $cyprus-color;

        @include min-width(lg) {
            font-size: 3.5rem;
        }
    }
}

iframe {
    width: 100%;
    min-height: 77vh;

    @include min-width(xl) {
        width: 55%;
        margin: 0 auto;
    }
}
</style>